import { Model } from './model';

export enum EnrichedContactStatus {
  PENDING = 'pending',
  ENRICHED = 'enriched',
  FAILED = 'failed',
}

export enum InternalRole {
  ACCOUNT_MANAGER = 'account_manager',
  ACCOUNT_EXECUTIVE = 'account_executive',
  CSM = 'csm',
}

export enum ExternalRole {
  BILLING_CONTACT = 'billing_contact',
  EXECUTIVE = 'executive',
  ECONOMIC_USER = 'economic_user',
}

export const EXTERNAL_ROLE_LABELS: Record<ExternalRole, string> = {
  [ExternalRole.BILLING_CONTACT]: 'Billing Contact',
  [ExternalRole.EXECUTIVE]: 'Executive',
  [ExternalRole.ECONOMIC_USER]: 'Economic User',
};

export const INTERNAL_ROLE_LABELS: Record<InternalRole, string> = {
  [InternalRole.ACCOUNT_MANAGER]: 'Account Manager',
  [InternalRole.ACCOUNT_EXECUTIVE]: 'Account Executive',
  [InternalRole.CSM]: 'CSM',
};

export class Contact extends Model {
  id: string;

  email: string;

  family_name: string | null;

  given_name: string | null;

  is_primary_email: boolean;

  is_primary_phone: boolean;

  is_email_disabled: boolean;

  is_phone_disabled: boolean;

  is_sms_disabled: boolean;

  partner_id: string;

  phone: string | null;

  job_title: string | null;

  internal_role?: InternalRole | null;

  external_role?: ExternalRole;

  custom_fields: Record<string, { label: string; value: string }> | null;

  _name: string | null;

  get initials(): string {
    if (this.name) {
      return this.name
        .trim()
        .split(' ')
        .slice(0, 2)
        .map((n) => n.substring(0, 1))
        .join('');
    }

    return this.given_name && this.family_name
      ? `${this.given_name.substring(0, 1)}${this.family_name.substring(0, 1)}`
      : '';
  }

  get name(): string {
    const parts: string[] = [];
    if (this.given_name) parts.push(this.given_name);
    if (this.family_name) parts.push(this.family_name);

    if (parts.length) return parts.join(' ');

    if (this._name) return this._name;

    return '';
  }

  /* This indicates if this is just a contact we define on the front end for visual purposes */
  get isPartnerAccountContact(): boolean {
    return this.id === '-1';
  }

  get isInternal(): boolean | undefined {
    return !!this.internal_role;
  }
}

export interface CreateContactSchema {
  email: string;

  family_name?: string;

  given_name?: string;

  is_primary_phone?: boolean;

  is_primary_email?: boolean;

  phone: string | null;

  internal_role?: InternalRole | null;

  external_role?: ExternalRole | null;
}

export class ExternalContact extends Contact {
  status: EnrichedContactStatus;

  source: string;

  source_provider_id: string;

  domain: string;
}
